import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { SimpleProp } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { GeneratedSite } from "building-types/site/site";
import {
  type SiteShape,
  LandscapeType,
  type HeightBasedShape,
  type LandscapeSiteShape,
  SiteShapeType,
  type RetainingWallSiteShape,
} from "building-types/site/siteShape";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import type { Table } from "common/data/table";
import { toTitleCase } from "common/utils/strings";
import { makeXYPlane } from "doc/measurementUtils";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { aabb2Center } from "geom/aabb2";
import type { BRep, Face } from "geom/brep";
import {
  brepNew,
  faceAABB3,
  faceNew,
  facesAABB3,
  faceTopoNew,
  projectWorldXYPolyLine2ToFaceUV,
  verticalFacesByPolyLine3AndBottomZ,
} from "geom/brep";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import { extrudeFace, extrudePolyLine2, extrusionToBRep } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { polyLine2Thicken } from "geom/offset";
import { polyLine2OnPlane } from "geom/plane";
import { intersectBRep } from "geom/polyclip/setOps3";
import { polygonSubtract, polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Centroid,
  polyLine2Close,
  polyLine2Copy,
  polyLine2Length,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Edge, polyLine3EdgeCount, polyLine3EvalEdge, type PolyLine3 } from "geom/polyLine3";
import { trimRailWithClosedPolyLine } from "geom/trim";
import type { Vec2 } from "geom/vec2";
import { groundLevelId } from "../level/levelTools";
import type { SiteZGeom } from "./siteUtils";
import { calculateRelativeHeight, getSiteZ, siteShapePickHandle, SITE_THICKNESS } from "./siteUtils";

export function collectSubtractionOutlines(
  siteShapes: SiteShape[],
  levels: Table<Level>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
): PolyLine2 {
  const subtractionOutlines: PolyLine2[] = [];
  const groundLevel = groundLevelId(levels);
  if (!groundLevel) {
    return polyLine2New([]);
  }

  const groundRooms = roomsOnLevel(groundLevel, rooms, roomsByLevel);

  try {
    if (Object.values(rooms)?.length > 0) {
      for (const room of groundRooms) {
        if (room.type !== RoomType.BAY_WINDOW) {
          const roomOutline = polyLine2Copy(room.outline);
          polyLine2SetCCW(roomOutline);
          subtractionOutlines.push(roomOutline);
        }
      }
    }

    // Collect driveway outlines
    for (const shape of siteShapes) {
      if (shape.type === SiteShapeType.SITE_SUBTRACT || (shape.type === SiteShapeType.DRIVEWAY && !shape.isConformal)) {
        if (shape.geom.length >= 6) {
          const closedOutline = polyLine2Close(shape.geom);
          polyLine2SetCCW(closedOutline);
          subtractionOutlines.push(closedOutline);
        }
      }
    }

    // Filter and prepare outlines for union
    const outlinesToUnion = subtractionOutlines
      .filter((outline) => outline.length >= 6) // Minimum 3 points
      .map((outline) => [polyLine2New(outline)]); // Ensure each outline is a proper PolyLine2

    if (outlinesToUnion.length === 0) {
      return polyLine2New([]);
    }

    const unionResult = polygonUnion(outlinesToUnion, true);

    if (!unionResult || unionResult.length === 0) {
      console.warn("Union returned null or empty result");
      return polyLine2New([]);
    }

    // Find the polygon with the most points
    let maxPolygon = polyLine2New([]);
    let maxLength = 0;

    for (const polygonSet of unionResult) {
      for (const polygon of polygonSet) {
        if (polygon.length > maxLength) {
          maxLength = polygon.length;
          maxPolygon = polygon;
        }
      }
    }

    return polyLine2New(maxPolygon);
  } catch (e) {
    console.warn("Failed to generate subtraction outline", e);
    return polyLine2New([]);
  }
}

export function createExtrusionBRep(outline: PolyLine2, baseHeight: number, height: number): BRep {
  const newOutline = polyLine2Close(outline);
  polyLine2SetCCW(newOutline);
  const extrusion = extrudePolyLine2(newOutline, makeXYPlane(0, 0, baseHeight), 0, 0, height + DIST_Z_FIGHT);
  return extrusionToBRep(extrusion);
}

export function processContourShape(
  shape: HeightBasedShape,
  contourPolylines: PolyLine3[],
  siteElevationReference: number = 0,
): void {
  const relativeHeight = calculateRelativeHeight(shape.zHeight, siteElevationReference);
  const polyline3D = polyLine2OnPlane(makeXYPlane(0, 0, relativeHeight), shape.geom);
  contourPolylines.push(polyline3D);
}

export function processSiteOverrideShape(
  shape: HeightBasedShape,
  heightOverridePls: PolyLine3[],
  siteElevationReference: number = 0,
): void {
  const relativeHeight = calculateRelativeHeight(shape.zHeight, siteElevationReference);
  const polyline3D = polyLine2OnPlane(makeXYPlane(0, 0, relativeHeight), shape.geom);
  heightOverridePls.push(polyline3D);
}

export function processSiteFillShape(
  shape: HeightBasedShape,
  padPolylines: PolyLine3[],
  generatedSite: GeneratedSite,
  siteElevationReference: number = 0,
): void {
  const relativeHeight = calculateRelativeHeight(shape.zHeight, siteElevationReference);
  const polyline3D = polyLine2OnPlane(makeXYPlane(0, 0, relativeHeight), shape.geom);
  const siteFillBRep = createOutlineBRep(shape.geom, relativeHeight, SITE_THICKNESS);
  if (siteFillBRep) {
    padPolylines.push(polyline3D);
    generatedSite.grassGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(
        siteFillBRep,
        exteriorMaterialOverrideNew(shape.material),
      ),
      pickHandle: siteShapePickHandle(shape, generatedSite.id),
    });
  }
}

export function processSiteSubtractShape(
  shape: HeightBasedShape,
  siteSubtractPolyLines: PolyLine3[],
  generatedSite: GeneratedSite,
  siteElevationReference: number = 0,
): void {
  const relativeHeight = calculateRelativeHeight(shape.zHeight, siteElevationReference);
  const polyline3D = polyLine2OnPlane(makeXYPlane(0, 0, relativeHeight), shape.geom);
  if (shape.zHeight) {
    const subtractBRep = createOutlineBRep(shape.geom, relativeHeight, shape.height ?? 72);
    if (subtractBRep) {
      siteSubtractPolyLines.push(polyline3D);
      generatedSite.curbGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(
          subtractBRep,
          exteriorMaterialOverrideNew("foundation0" as ExteriorMaterial),
        ),
        pickHandle: siteShapePickHandle(shape, generatedSite.id),
      });
    }
  }
}

export function processRetainingWallShape(
  shape: RetainingWallSiteShape,
  generatedSite: GeneratedSite,
  siteElevationReference: number = 0,
): void {
  const relativeHeight = calculateRelativeHeight(shape.zHeight, siteElevationReference);

  const thickenedOutline = polyLine2Thicken(shape.geom, 6);
  polyLine2SetCW(thickenedOutline);
  const face = faceNew([thickenedOutline], makeXYPlane(0, 0, relativeHeight));
  const extrusion = extrudeFace(face, 0, 0, shape.height ?? 72);
  generatedSite.curbGeom.push({
    group: new GeomTagGroup<ExteriorMaterialOverride>().add(extrusion, exteriorMaterialOverrideNew(shape.material)),
    pickHandle: siteShapePickHandle(shape, generatedSite.id),
  });
}

export function processLandscapeShape(
  shape: LandscapeSiteShape,
  landscapePolylines: PolyLine3[],
  generatedSite: GeneratedSite,
  terrainGeom?: SiteZGeom,
  siteElevationReference: number = 0,
): void {
  const bushTypes = [
    "Plant_Bush_36x24x24_Box_01",
    "Plant_Bush_36x24x24_Box_02",
    "Plant_Bush_36x24x24_Box_03",
    "Plant_Bush_36x36x36_Round_01",
    "Plant_Bush_36x36x36_Round_02",
    "Plant_Bush_36x36x36_Round_03",
    "Plant_Bush_36x36x72_Tall_01",
    "Plant_Bush_48x30x30_Oval_01",
    "Plant_Bush_48x48x48_Round_01",
    "Plant_Bush_24x24x24_Strands_01",
  ];

  const polyline3D = polyLine2OnPlane(makeXYPlane(0, 0, siteElevationReference), shape.geom);
  landscapePolylines.push(polyline3D);

  if (shape.landscapeType === LandscapeType.SINGLE_TREE) {
    const centroid = polyLine2Centroid(shape.geom);
    if (!centroid) {
      return;
    }
    const [cX, cY] = centroid;
    let zHeight = -12;
    if (terrainGeom) {
      const terrainZ = getSiteZ(terrainGeom, cX, cY);
      zHeight += terrainZ;
    }

    const siteProp: SimpleProp = {
      type: "SIMPLE",
      propName: "Lot_GenericTestTree_Center",
      x: cX,
      y: cY,
      z: zHeight,
      castShadow: true,
      scale: 1.0,
    };

    generatedSite.propPlacements.push(siteProp);
    return;
  }

  // Heuristic for populating landscape props
  const divisor = shape.landscapeType === LandscapeType.TREES ? 150 : 10;
  const propCount = Math.floor(polyLine2Length(shape.geom) / divisor);
  const bounds = polyLine2AABB2(shape.geom);

  for (let i = 0; i < propCount; i++) {
    // Use a unique seed for each attempt within each prop placement
    const uniqueSeed = i * 1000;
    const randomPoint = getRandomPointInPolygon(shape.geom, bounds, uniqueSeed);
    if (randomPoint) {
      let plantType = "Lot_GenericTestTree_Center";

      if (shape.landscapeType === LandscapeType.BUSHES) {
        const randomIndex = Math.floor(seededRandom(uniqueSeed + 1) * bushTypes.length);
        plantType = bushTypes[randomIndex];
      }

      const randomScale = 0.8 + seededRandom(uniqueSeed + 2) * 0.4;

      // Calculate the z-height with respect to the site elevation reference
      let zHeight = -12;
      if (terrainGeom) {
        const terrainZ = getSiteZ(terrainGeom, randomPoint[0], randomPoint[1]);
        zHeight += terrainZ;
      }

      const siteProp: SimpleProp = {
        type: "SIMPLE",
        propName: plantType,
        x: randomPoint[0],
        y: randomPoint[1],
        z: zHeight,
        castShadow: true,
        scale: randomScale,
      };

      generatedSite.propPlacements.push(siteProp);
    }
  }
}

export function getRandomPointInPolygon(
  polygon: PolyLine2,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  seed: number,
): Vec2 | null {
  const maxAttempts = 50;
  let attempts = 0;

  while (attempts < maxAttempts) {
    const x = bounds.minX + seededRandom(seed + attempts * 2) * (bounds.maxX - bounds.minX);
    const y = bounds.minY + seededRandom(seed + attempts * 2 + 1) * (bounds.maxY - bounds.minY);

    if (isPointInPolygon([x, y], polygon)) {
      return [x, y];
    }
    attempts++;
  }

  return null;
}

export function isPointInPolygon(point: Vec2, polygon: PolyLine2): boolean {
  let inside = false;
  const x = point[0];
  const y = point[1];

  for (let i = 0, j = polygon.length - 2; i < polygon.length; i += 2) {
    const xi = polygon[i];
    const yi = polygon[i + 1];
    const xj = polygon[j];
    const yj = polygon[j + 1];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }

    j = i;
  }

  return inside;
}

export function createOutlineBRep(
  outline: PolyLine2 | undefined,
  baseHeight: number,
  height: number,
): BRep | undefined {
  if (outline) {
    return createExtrusionBRep(outline, baseHeight - height, height);
  }
  return undefined;
}

export function createTerrainSurfaceBRep(faces: Face[]): BRep {
  if (faces.length === 0) {
    return brepNew([], []);
  }

  const faceTopos = faces.map(() => faceTopoNew());
  const terrainBRep = brepNew(faces, faceTopos);

  return terrainBRep;
}

export function seededRandom(seed: number): number {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
}

export function intersectBRepWithOutline(brep: BRep, outline: PolyLine2): Face[] {
  const resultFaces: Face[] = [];
  const outlineExtrusion = extrudePolyLine2(outline, makeXYPlane(0, 0, -1000), 0, 0, 2000);
  const outlineBRep = extrusionToBRep(outlineExtrusion);

  for (const face of brep.faces) {
    if (faceOverlapsOutline(face, outline)) {
      const faceExtrusion = extrudeFace(face, 0, 0, 1);
      const faceBRep = extrusionToBRep(faceExtrusion);

      const faces = intersectBRep(faceBRep, outlineBRep);
      resultFaces.push(...faces);
    }
  }
  return resultFaces;
}

export function faceOverlapsOutline(face: Face, outline: PolyLine2): boolean {
  const outlineAABB = polyLine2AABB2(outline);

  const faceAABB3 = facesAABB3([face]);
  if (faceAABB3) {
    const { minX, maxX, minY, maxY } = faceAABB3;
    if (
      maxX >= outlineAABB.minX - DIST_EPSILON &&
      minX <= outlineAABB.maxX + DIST_EPSILON &&
      maxY >= outlineAABB.minY - DIST_EPSILON &&
      minY <= outlineAABB.maxY + DIST_EPSILON
    ) {
      return true;
    }
  }
  return false;
}

const SiteShapeTypeToViewFilterKey: {
  [key in SiteShapeType]?: keyof ViewFilter;
} = {
  [SiteShapeType.CONTOUR]: "siteShapesContour",
  [SiteShapeType.DRIVEWAY]: "siteShapesDriveway",
  [SiteShapeType.SITE_OVERRIDE]: "siteShapesHeightOverrides",
  [SiteShapeType.SITE_FILL]: "siteShapesSiteFill",
  [SiteShapeType.SITE_SUBTRACT]: "siteShapesSiteSubtract",
  [SiteShapeType.RETAINING_WALL]: "siteShapesRetainingWall",
  [SiteShapeType.LANDSCAPE]: "siteShapesLandscape",
  [SiteShapeType.WALK]: "siteShapesWalkway",
  [SiteShapeType.ROAD]: "siteShapesRoad",
};

export function siteShapeTypeToViewFilterKey(
  siteShapeType: SiteShapeType,
  defaultViewFilter: keyof ViewFilter = "siteShapes",
): keyof ViewFilter {
  return SiteShapeTypeToViewFilterKey[siteShapeType] ?? defaultViewFilter;
}

export function siteShapeNameToViewFilterKey(viewFilter: keyof ViewFilter): string {
  switch (viewFilter) {
    case "siteShapesContour":
      return "Contour";
    case "siteShapesDriveway":
      return "Driveway";
    case "siteShapesHeightOverrides":
      return "Height Overrides";
    case "siteShapesSiteFill":
      return "Site Fill";
    case "siteShapesSiteSubtract":
      return "Rear Porch";
    case "siteShapesRetainingWall":
      return "Retaining Wall";
    case "siteShapesLandscape":
      return "Landscape";
    case "siteShapesWalkway":
      return "Walkway";
    case "siteShapesRoad":
      return "Road";
    default:
      return toTitleCase(viewFilter.replace("siteShape", "").trimEnd());
  }
}

/** A subtractable outline needs at least 3 points, i.e. 6 PolyLine2 components. */
const MIN_SUBTRACTION_OUTLINE_LENGTH = 6;

/**
 * Trim faces by an outline using2D boolean ops on each face plane.
 * - Discards faces fully inside the outline
 * - Keeps fully outside faces as-is
 * - Trims intersected faces, keeping only the outside portions
 * - Creates vertical faces for the edges of the outline down to the base height
 */
export function subtractOutlineFromFacesAndExtrudeToBaseHeight(
  faces: Face[],
  outline: PolyLine2,
  baseHeight: number,
): { faces: Face[]; intersected: Face[] } {
  // Nothing to subtract, which is normal for a site-only plan. Return the faces untouched rather
  // than handing polygon-clipping an empty ring per face, which throws and gets logged.
  if (outline.length < MIN_SUBTRACTION_OUTLINE_LENGTH) {
    return { faces, intersected: [] };
  }

  const resultFaces: Face[] = [];
  const intersectedFaces: Face[] = [];

  const outlineAABB = polyLine2AABB2(outline);

  for (let i = 0; i < faces.length; i++) {
    const face = faces[i];

    /**
     * This validation is tightly coupled to faces being
     * tris or quads (or closed face loop without inner loops... aka... holes).
     * Loft grid creates tri faces... so this warn is just a smoke signal
     * in case, someone tries this function on faces with inner loops.
     */
    const { faceLoopUV, success } = validateFaceLoopHasNoHolesAndIsClosed(face, i);
    if (!success) {
      console.warn(
        `Face at index ${i} has holes or is not closed; outline will not be subtracted and extruded from this face`,
      );
      return { faces: [], intersected: [] };
    }

    // Quick accept if face bounding box is outside of outline bounding box
    const faceAABB = faceAABB3(face, 0);
    if (
      faceAABB.maxX < outlineAABB.minX ||
      faceAABB.minX > outlineAABB.maxX ||
      faceAABB.maxY < outlineAABB.minY ||
      faceAABB.minY > outlineAABB.maxY
    ) {
      resultFaces.push(face);
      continue;
    }

    const outlineUV = projectWorldXYPolyLine2ToFaceUV(outline, face);
    const diffPolys = polygonSubtract([[faceLoopUV], [outlineUV]]);

    if (diffPolys.length === 0) {
      const [cx, cy] = aabb2Center(faceAABB);

      // filter out faces that are completely inside the outline
      if (classifyContainmentVec2PolyLine2(cx, cy, outline) === Vec2PolyLine2ContainmentClass.INSIDE) {
        continue;
      }

      // this face is so close to the outline that it should be kept as-is
      resultFaces.push(face);

      /**
       * since we are keeping this face, and it doesn't have a subtraction result,
       * check if each of the edges are on the outline. If within .1 inches, then show
       * the vertical face; otherwise, there will be a gap.
       */
      const facePl3 = polyLine2OnPlane(face.plane, faceLoopUV);
      for (let i = 0; i < polyLine3EdgeCount(facePl3); i++) {
        const [x, y] = polyLine3EvalEdge(facePl3, i, 0.5);
        const { dist } = closestPointPolyLine2(outline, x, y);
        // if the face edge is on the outline, then add a vertical face to the base height
        if (dist < DIST_Z_FIGHT) {
          resultFaces.push(...verticalFacesByPolyLine3AndBottomZ(polyLine3Edge(facePl3, i), baseHeight, true));
        }
      }
      continue;
    }

    const { insideRails } = trimRailWithClosedPolyLine(outlineUV, faceLoopUV);
    for (const insideRail of insideRails) {
      //These are the vertical faces at the outline cut that extrude towards the base of the shape.
      const edgePl3 = polyLine2OnPlane(face.plane, insideRail);
      resultFaces.push(...verticalFacesByPolyLine3AndBottomZ(edgePl3, baseHeight, true));
      /**
       * Replace above line with the line below this comment, if you'd like the extrusion into the site to have a "soil" texture.
       * The implementation previous to this one doesn't differentiate (shows "grass" texture), so intersectedFaces is not used and is left empty.
       */
      //intersectedFaces.push(...verticalFacesAlongPolyLineOnPlane(insideRail, face.plane, baseHeight));
    }

    // this is remaining face after the subtraction
    for (const poly of diffPolys) {
      if (poly.length === 0) {
        continue;
      }
      const loops: PolyLine2[] = [];
      for (const loop of poly) {
        loops.push(loop);
      }
      if (loops.length > 0) {
        resultFaces.push(faceNew(loops, face.plane));
      }
    }
  }

  return { faces: resultFaces, intersected: intersectedFaces };
}

interface ValidateFaceLoopResultSuccess {
  readonly faceLoopUV: PolyLine2;
  readonly success: true;
}

interface ValidateFaceLoopResultFailure {
  readonly faceLoopUV: null;
  readonly success: false;
}

type ValidateFaceLoopResult = ValidateFaceLoopResultSuccess | ValidateFaceLoopResultFailure;

function validateFaceLoopHasNoHolesAndIsClosed(face: Face, faceIndex: number): ValidateFaceLoopResult {
  if (face.loops.length !== 1) {
    console.warn(`Face at index ${faceIndex} has ${face.loops.length} loops; expected exactly 1`);
    return { faceLoopUV: null, success: false };
  }
  const faceLoopUV = face.loops[0];

  // check Xs first (eager failure)
  if (Math.abs(faceLoopUV[0] - faceLoopUV[faceLoopUV.length - 2]) > DIST_EPSILON) {
    console.warn(`Face at index ${faceIndex} loop not closed within tolerance`);
    return { faceLoopUV: null, success: false };
  }

  // check Ys second
  if (Math.abs(faceLoopUV[1] - faceLoopUV[faceLoopUV.length - 1]) > DIST_EPSILON) {
    console.warn(`Face at index ${faceIndex} loop not closed within tolerance`);
    return { faceLoopUV: null, success: false };
  }
  return { faceLoopUV, success: true };
}
